import { useCallback, useContext, useEffect, useRef } from 'react';

// Module type matches @atlaskit/simple-xdm host.defineModule() - not exported from package host entry
type Module = { [x: string]: any };

import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import type {
	AbortOptions,
	ExperienceTrackerAPI,
	FailOptions,
	StartOptions,
	SucceedOptions,
} from '@atlassian/experience-tracker/ExperienceTracker';

import {
	CHANNEL_NAME,
	EXPERIENCE_FORWARDING_MODULE_NAME,
	type ExperienceForwardingModule,
	PASSTHROUGH_PROPS_MODULE_NAME,
	type PassThroughPropsModule,
	PROPS_UPDATED,
} from './channel';
import type { IframePassThroughProps } from './IframeElementType';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

let host: any = null;
// simple-xdm relies on window object. It is not available in SSR.
if (typeof window !== 'undefined') {
	// @atlaskit/simple-xdm/plugin is an ES-module default export, so when loaded via require(), we need to read `.default` (otherwise we get the module namespace object)
	const requiredHost = require('@atlaskit/simple-xdm/host');
	host = requiredHost?.default ?? requiredHost;
}

function defineModule<T extends Module>(name: string, module: T) {
	host.defineModule(name, module);
}

const useExperienceForwardingModule = () => {
	const experienceTrackerRef = useRef<ExperienceTrackerAPI>();
	const { uniqueKey } = useUniqueKey();
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (experienceTrackerRef.current !== experienceTracker) {
		defineModule<ExperienceForwardingModule>(
			uniqueKey
				? `${EXPERIENCE_FORWARDING_MODULE_NAME}_${uniqueKey}`
				: EXPERIENCE_FORWARDING_MODULE_NAME,
			{
				start(options: StartOptions) {
					experienceTracker.start(options);
				},
				abort(options: AbortOptions) {
					experienceTracker.abort(options);
				},
				succeed(options: SucceedOptions) {
					experienceTracker.succeed(options);
				},
				fail(options: FailOptions) {
					experienceTracker.fail(options);
				},
			},
		);
		experienceTrackerRef.current = experienceTracker;
	}
};

const usePassThroughPropsModule = (
	props: IframePassThroughProps & {
		onContentLoadStarted?: () => void;
	},
): void => {
	const { onContentLoadStarted, ...passThroughProps } = props;
	const dispatchPropsUpdated = useCallback(() => {
		host.dispatch(
			PROPS_UPDATED,
			{
				addon_key: passThroughProps.uniqueKey ?? CHANNEL_NAME,
				key: passThroughProps.uniqueKey ?? CHANNEL_NAME,
			},
			{
				message: JSON.stringify(passThroughProps),
			},
		);

		/**
		 * Disable react-hooks/exhaustive-deps linting, the dependency has passThroughProps spreading on its keys and values
		 * This should be able to handle any changes to the `passThroughProps` would recall the effect function.
		 **/
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...Object.keys(passThroughProps),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...Object.values(passThroughProps),
		passThroughProps.uniqueKey,
	]);

	const dispatchPropsUpdatedRef = useRef<typeof dispatchPropsUpdated>();

	if (dispatchPropsUpdatedRef.current !== dispatchPropsUpdated) {
		defineModule<PassThroughPropsModule>(
			passThroughProps.uniqueKey
				? `${PASSTHROUGH_PROPS_MODULE_NAME}_${passThroughProps.uniqueKey}`
				: PASSTHROUGH_PROPS_MODULE_NAME,
			{
			getProps: () => {
				onContentLoadStarted?.();
				dispatchPropsUpdated();
			},
			navigationPolicyNavigate: passThroughProps.navigationPolicy?.navigate,
			onPublish: passThroughProps?.onPublish,
		},
		);

		dispatchPropsUpdatedRef.current = dispatchPropsUpdated;
	}

	useEffect(dispatchPropsUpdated, [dispatchPropsUpdated]);
};

export function useIframeCommunicationChannel(
	iframeSrc: string,
	passThroughProps: Parameters<typeof usePassThroughPropsModule>[0],
): any {
	const channelRef = useRef<ReturnType<typeof host.create>>();

	const uniqueKey = passThroughProps.uniqueKey ?? CHANNEL_NAME;

	useExperienceForwardingModule();
	usePassThroughPropsModule(passThroughProps);

	if (channelRef.current?.src !== iframeSrc) {
		channelRef.current = host.create({
			addon_key: uniqueKey,
			key: uniqueKey,
			url: iframeSrc,
			options: {
				globalOptions: {},
				// `hostFrameOffset` - Offset number specified to find iframe's host frame, it will be `window.top` if not specified.
				// Since EP frame can be embedded by middle layer frame(s), we set `hostFrameOffset` to 1 to ensure EP iframe's direct parent is the host frame.
				hostFrameOffset: 1,
				hostOrigin: window.location.origin,
			},
		});
	}
	return channelRef.current;
}
